import {
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  File as FileIcon,
  type LucideIcon,
} from "lucide-react";

import { ENV_VARS } from "@/constants/env";
import { API_COMPANY_FILE_PRESIGNED } from "@/constants/apiEndpoints";
import { FILE_CATEGORY } from "@/types/(company)/files.interface";

export function formatBytes(bytes: number | null | undefined, decimals = 1): string {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const idx = Math.min(i, units.length - 1);
  const size = bytes / Math.pow(k, idx);
  return `${size.toFixed(idx === 0 ? 0 : decimals)} ${units[idx]}`;
}

const DOCUMENT_MIME_TYPES = new Set<string>([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/rtf",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
]);

const ARCHIVE_MIME_TYPES = new Set<string>([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-7z-compressed",
  "application/x-rar-compressed",
  "application/x-tar",
  "application/gzip",
]);

const SPREADSHEET_MIME_TYPES = new Set<string>([
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/vnd.oasis.opendocument.spreadsheet",
]);

export function categorizeMime(mime?: string | null): FILE_CATEGORY {
  if (!mime) return FILE_CATEGORY.OTHER;
  const lower = mime.toLowerCase();
  if (lower.startsWith("image/")) return FILE_CATEGORY.IMAGE;
  if (lower.startsWith("video/")) return FILE_CATEGORY.VIDEO;
  if (lower.startsWith("audio/")) return FILE_CATEGORY.AUDIO;
  if (DOCUMENT_MIME_TYPES.has(lower)) return FILE_CATEGORY.DOCUMENT;
  if (ARCHIVE_MIME_TYPES.has(lower)) return FILE_CATEGORY.ARCHIVE;
  return FILE_CATEGORY.OTHER;
}

export function getFileIcon(mime?: string | null): LucideIcon {
  const category = categorizeMime(mime);
  if (category === FILE_CATEGORY.IMAGE) return FileImage;
  if (category === FILE_CATEGORY.VIDEO) return FileVideo;
  if (category === FILE_CATEGORY.AUDIO) return FileAudio;
  if (category === FILE_CATEGORY.ARCHIVE) return FileArchive;
  if (category === FILE_CATEGORY.DOCUMENT) {
    if (mime && SPREADSHEET_MIME_TYPES.has(mime.toLowerCase())) {
      return FileSpreadsheet;
    }
    return FileText;
  }
  return FileIcon;
}

export function getCategoryColor(category: FILE_CATEGORY): string {
  switch (category) {
    case FILE_CATEGORY.IMAGE:
      return "text-emerald-500 bg-emerald-50";
    case FILE_CATEGORY.VIDEO:
      return "text-purple-500 bg-primary";
    case FILE_CATEGORY.AUDIO:
      return "text-amber-500 bg-amber-50";
    case FILE_CATEGORY.DOCUMENT:
      return "text-info bg-info-muted";
    case FILE_CATEGORY.ARCHIVE:
      return "text-orange-500 bg-orange-50";
    default:
      return "text-gray-500 bg-gray-100";
  }
}

export function isPreviewableImage(mime?: string | null): boolean {
  return !!mime && mime.toLowerCase().startsWith("image/");
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

export function stripExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

export function buildFilePresignedUrl(fileId: string): string {
  const base = ENV_VARS.API_INSTANCE?.replace(/\/$/, "") ?? "";
  return `${base}${API_COMPANY_FILE_PRESIGNED(fileId)}`;
}

export function buildFileDownloadUrl(fileId: string, filename: string): string {
  const params = new URLSearchParams({ download: "true", filename });
  return `${buildFilePresignedUrl(fileId)}?${params.toString()}`;
}

/**
 * An absolute http(s) URL — the only kind a file link or image from the API
 * (chat attachments, AI-generated files) may render as `href` / `src`. Anything
 * else (`javascript:`, `data:`, relative junk) is dropped by the caller.
 */
export const isHttpUrl = (url: unknown): url is string =>
  typeof url === "string" && /^https?:\/\//i.test(url);
